import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Users, Snowflake, Car, Utensils, Sparkles, MapPin, Check, X, ShoppingBag, CalendarCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function Flag({ ok, yes, no }) {
  return (
    <span className={`flex items-center gap-1 text-sm ${ok ? "text-emerald-700" : "text-rose-700"}`}>
      {ok ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />} {ok ? yes : no}
    </span>
  );
}

export default function HallDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { data: hall, isLoading } = useQuery({ queryKey: ["hall", id], queryFn: async () => (await api.get(`/halls/${id}`)).data });

  const [inqOpen, setInqOpen] = useState(false);
  const emptyInq = { customer_name: "", phone: "", email: "", event_type: "", event_date: "", message: "" };
  const [inq, setInq] = useState(emptyInq);
  const [sending, setSending] = useState(false);
  const setF = (k, v) => setInq((p) => ({ ...p, [k]: v }));

  if (isLoading) return <div className="max-w-5xl mx-auto px-4 py-10 text-muted-foreground">Loading…</div>;
  if (!hall) return <div className="max-w-5xl mx-auto px-4 py-10">Hall not found.</div>;

  const book = () => {
    addItem(
      { id: hall.id, name: `${hall.name} (${hall.hall_type})`, price: hall.price, unit: "per event", image: hall.cover },
      { id: hall.id, name: hall.name },
    );
    toast.success("Hall added to cart");
    navigate("/cart");
  };

  const submitInquiry = async () => {
    if (!inq.customer_name || !inq.phone || !inq.email || !inq.event_type) {
      return toast.error("Please fill name, phone, email and event type");
    }
    setSending(true);
    try {
      await api.post("/enquiries", { ...inq, hall_id: hall.id, hall_name: hall.name });
      toast.success("Availability request sent! We'll get back to you shortly.");
      setInqOpen(false); setInq(emptyInq);
    } catch { toast.error("Could not send request. Please try again."); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 pb-24" data-testid="hall-detail">
      <div className="rounded-3xl overflow-hidden border border-stone-200">
        {hall.cover ? <img src={imgUrl(hall.cover)} alt={hall.name} className="h-64 md:h-80 w-full object-cover" /> : <div className="h-64 w-full bg-gradient-to-br from-primary/30 to-secondary/30" />}
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap mt-5">
        <div>
          <span className="text-sm text-primary font-semibold">{hall.hall_type}</span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{hall.name}</h1>
          {(hall.address || hall.city) && <p className="text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-4 w-4" /> {[hall.address, hall.city].filter(Boolean).join(", ")}</p>}
        </div>
        <div className="text-right">
          <p className="font-display font-extrabold text-2xl">{inr(hall.price)}<span className="text-sm text-muted-foreground font-normal"> / event</span></p>
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <button data-testid="hall-add-cart" onClick={book} className="h-11 px-6 bg-primary text-white rounded-full font-semibold flex items-center justify-center gap-2">
              <ShoppingBag className="h-4 w-4" /> Add to cart
            </button>
            <button data-testid="hall-inquiry-btn" onClick={() => setInqOpen(true)} className="h-11 px-6 border border-primary text-primary rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-primary/5">
              <CalendarCheck className="h-4 w-4" /> Check availability
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <div className="bg-card border border-stone-200 rounded-xl p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Seating</p><p className="font-bold text-lg">{hall.seat_capacity || "—"}</p></div>
        <div className="bg-card border border-stone-200 rounded-xl p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><Utensils className="h-3.5 w-3.5" /> Dining</p><p className="font-bold text-lg">{hall.dining_capacity || "—"}</p></div>
        <div className="bg-card border border-stone-200 rounded-xl p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><Snowflake className="h-3.5 w-3.5" /> AC</p><p className="font-bold text-lg">{hall.ac}</p></div>
        <div className="bg-card border border-stone-200 rounded-xl p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><Car className="h-3.5 w-3.5" /> Parking</p><p className="font-bold text-lg">{hall.parking_capacity || "—"}</p></div>
      </div>

      <div className="flex flex-wrap gap-4 mt-4">
        <Flag ok={hall.outside_catering_allowed} yes="Outside catering allowed" no="Outside catering not allowed" />
        <Flag ok={hall.outside_decoration_allowed} yes="Outside decoration allowed" no="Outside decoration not allowed" />
      </div>

      {hall.description && <p className="text-muted-foreground mt-6 whitespace-pre-wrap">{hall.description}</p>}

      {(hall.amenities || []).length > 0 && (
        <div className="mt-6">
          <h2 className="font-display font-bold text-xl mb-2 flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Amenities</h2>
          <div className="flex flex-wrap gap-2">
            {hall.amenities.map((a, i) => <span key={i} className="text-sm px-3 py-1 rounded-full bg-accent">{a}</span>)}
          </div>
        </div>
      )}

      {(hall.gallery || []).length > 0 && (
        <div className="mt-8" data-testid="hall-gallery">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Photos</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {hall.gallery.map((src, i) => <img key={i} src={imgUrl(src)} alt={`${hall.name} ${i + 1}`} className="w-full aspect-square object-cover rounded-2xl border border-stone-200" loading="lazy" />)}
          </div>
        </div>
      )}

      <Dialog open={inqOpen} onOpenChange={setInqOpen}>
        <DialogContent data-testid="hall-inquiry-dialog">
          <DialogHeader>
            <DialogTitle>Check availability — {hall.name}</DialogTitle>
            <DialogDescription>Share your details and event date. Our team will confirm availability and pricing — no payment now.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 mt-2">
            <Input data-testid="inq-name" placeholder="Your name" value={inq.customer_name} onChange={(e) => setF("customer_name", e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="inq-phone" placeholder="Phone" value={inq.phone} onChange={(e) => setF("phone", e.target.value)} />
              <Input data-testid="inq-email" type="email" placeholder="Email" value={inq.email} onChange={(e) => setF("email", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="inq-event-type" placeholder="Event type (e.g. Wedding)" value={inq.event_type} onChange={(e) => setF("event_type", e.target.value)} />
              <Input data-testid="inq-event-date" type="date" value={inq.event_date} onChange={(e) => setF("event_date", e.target.value)} />
            </div>
            <Textarea data-testid="inq-message" placeholder="Anything else we should know?" value={inq.message} onChange={(e) => setF("message", e.target.value)} />
            <button data-testid="inq-submit" onClick={submitInquiry} disabled={sending} className="h-11 bg-primary text-white rounded-full font-semibold disabled:opacity-60">
              {sending ? "Sending…" : "Send availability request"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
